/**
 * ⭐⭐ WHAT AN AI SUGGESTION COSTS TO APPLY — the key and the quantity.
 *
 * The suggestion-fence path (`AIChat/SuggestionPreview.tsx`) has created tasks,
 * habits, routines, projects, abilities and templates **for free** since the day
 * it shipped, while every use the owner named as the POINT of AI — direction
 * statements, assessment interpretation, quest and program setup — was charged.
 * So the guardrail was on thinking and absent on the one behaviour it exists to
 * discourage: filling the app with actions nobody will use.
 *
 * ⚠️⚠️ **THE QUANTITY IS THE NUMBER OF INDEPENDENT ITEMS THAT LAND**, because a
 * flat fee per Apply is not a guardrail: one chat turn can return five tasks and
 * one click lands all five, so "give me ten habits" would cost the same as one —
 * bulk at a discount, which is backwards.
 *
 * ⭐ **A ROUTINE COUNTS AS ONE** (owner, 2026-08-14), even though it materialises
 * a habit per step, and a project counts as one however many phases it carries.
 * The rule is *what the user will see as a thing in their app afterwards*, not
 * how many rows the store writes. Charging per materialised step would punish
 * exactly the case the owner values — "these take a long time to set up and the
 * AI removes that friction".
 *
 * ⚠️⚠️ **`program` IS EXEMPT, AND THAT IS A DECISION, NOT AN OVERSIGHT.**
 * `program_generation` is ACTIVE at generation time, so charging its contents at
 * apply as well would be the only place in the app that bills one artifact
 * twice. → **If `program_generation` is ever flipped to draft (generation free,
 * the locked hybrid), delete this exemption in the same change** or programs
 * become the free route around every price below.
 *
 * ⚠️ `quest` charges **`create_quest`**, the same key the tool path uses. There
 * is deliberately no `apply_quest`: two prices for one artifact, decided by which
 * route the model happened to take, is the inconsistency being removed.
 */

export interface ApplyCharge {
  /** `ai_action_prices.key`. */
  key: string;
  /** How many independent items land. Always ≥ 1 when a charge is returned. */
  qty: number;
}

/**
 * Suggestion types that land exactly ONE thing, whatever they expand into.
 * ⚠️ A key here that is not in `ai_action_prices` charges NOTHING —
 * `spend_coin` selects `where key = … and active`, so an unknown key finds no
 * row and returns free. `applyPrices.test.ts` pins every key against the
 * migration that seeds it.
 */
const SINGLE_ITEM: Record<string, string> = {
  routine: 'apply_routine',
  project: 'apply_project',
  ability: 'apply_ability',
  template: 'apply_template',
  quest: 'create_quest',
};

/** Types that land one item per entry in `items`. */
const PER_ITEM: Record<string, string> = {
  tasks: 'apply_task',
  habits: 'apply_habit',
};

/**
 * ⚠️⚠️ A TASK FOLDER COSTS WHAT ITS TASKS COST LOOSE (`apply_task` × n, owner
 * 2026-09-17). The folder itself is free — charging it as one item would make
 * "put them in a folder" the cheap route around the per-task price, which is the
 * bulk-at-a-discount the header above rejects. Its tasks live in `tasks`, not
 * `items`, so it needs its own branch.
 */
const FOLDER_TYPE = 'taskFolder';

/**
 * What applying this suggestion costs, or `null` for nothing to charge.
 *
 * `null` covers three genuinely different cases, and all three are free:
 *  · an exempt type (`program`),
 *  · an unrecognised type (nothing is applied either),
 *  · a per-item type whose list is empty (nothing lands, so nothing is owed).
 */
export function applyChargeFor(suggestion: unknown): ApplyCharge | null {
  const s = suggestion as { type?: unknown; items?: unknown } | null | undefined;
  const type = typeof s?.type === 'string' ? s.type : '';
  if (!type) return null;

  if (type === FOLDER_TYPE) {
    const tasks = (s as { tasks?: unknown })?.tasks;
    const qty = Array.isArray(tasks) ? tasks.length : 0;
    return qty > 0 ? { key: PER_ITEM.tasks, qty } : null;
  }

  const perItem = PER_ITEM[type];
  if (perItem) {
    const items = s?.items;
    const qty = Array.isArray(items) ? items.length : 0;
    // ⚠️ Zero is not "free at qty 0" — it is nothing to apply. Returning a
    // charge here would call `spend_coin` for an empty batch.
    return qty > 0 ? { key: perItem, qty } : null;
  }

  const single = SINGLE_ITEM[type];
  return single ? { key: single, qty: 1 } : null;
}
